/* global fbq */
/**
 *
 *  Lifestyle Completed page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 07/03/2024.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { Title, Meta, Link as LinkMeta } from "react-meta-elements";

import { useAppState } from "../../../state";
import { usePrevState } from "../../../context/PrevStateProvider";
import { useRegion } from "../../../context/RegionProvider";

// Components
import Header from "../../Header/Header";
import Footer from "../../Footer/Footer";
import ScrollToTopOnMount from "../../ScrollToTopOnMount/ScrollToTopOnMount";
import Button from "../../Button/Button";
import EventCard from "../EventCard/EventCard";
import BreadcrumbNav from "../../BreadcrumbNav/BreadcrumbNav";

// Hooks
import useTheme from "../../ThemeProvider/useTheme/useTheme";

// Utils
import { clientWidth, getPartnerPictureUrl } from "../../../utils";
import { getMomentWithTz, getTz } from "../../../utils/date";
import { modalStyles, PAGE_SIZE } from "../../../constants";
import { groupBy } from "../../../utils/_";

// Icons
import ArrowLeftIcon from "../../../icons/ArrowLeftIcon";
import ArrowRightIcon from "../../../icons/ArrowRightIcon";

// Images

export default function LifestyleCompletedPage() {
  const { theme } = useTheme();
  const { region, city } = useRegion();
  const {
    user,
    hasTriedToAutoLogin,
    completedEvents,
    hasMorePastEvents,
    isPastEventsFetching,
    pastEventsPageLoaded,
    pastEventsLoaded,
    loadCompletedEvents,
    clearCompletedEventsLoaded,
  } = useAppState();

  const { prevState } = usePrevState();

  const heroRef = React.useRef(null);
  const sliderRef = React.useRef(null);
  const pastEventsLoadedRef = useRef(pastEventsLoaded);

  useEffect(() => {
    pastEventsLoadedRef.current = pastEventsLoaded;
  }, [pastEventsLoaded]);

  useEffect(
    () => {
      if (!hasTriedToAutoLogin) {
        return;
      }

      const userCity = user && user.city;

      // loadStartedEvents();
      // loadUpcomingEvents();

      // Check if the first record matches the pattern '/events/{id}', where {id} is a numerical value
      if (
        (!pastEventsLoaded &&
          !(
            prevState && prevState.some((item) => item.match(/^\/events\/\d+$/))
          )) ||
        !pastEventsLoaded
      ) {
        loadCompletedEvents(
          user && user.id,
          "COMPLETED", // Not used
          { sort: "start,desc", page: 0, size: PAGE_SIZE },
          user ? user.region : region /*process.env.REACT_APP_REGION*/,
          userCity || city
        );
      }

      // loadCompletedEvents();
    },
    [user, hasTriedToAutoLogin, prevState, pastEventsLoaded] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // For logout
  // FIXME If we open Event Details page for all users, here might be a problem
  useEffect(() => {
    if (!user && pastEventsLoadedRef.current) {
      clearCompletedEventsLoaded();
    }
  }, [user]);

  useEffect(() => {
    if (sliderRef.current && completedEvents && completedEvents.length) {
      setTimeout(() => {
        // Check if it's present, because we might be on different state already
        sliderRef.current &&
          sliderRef.current.scrollTo({
            left: sliderRef.current.scrollWidth,
            behavior: "smooth",
          });
        // sliderRef.current.scrollLeft = sliderRef.current.scrollWidth;
      }, 1000);
    }
  }, [sliderRef.current, completedEvents]);

  const isUserLoggedIn = !!(user && user.id);

  const pastEvents = groupBy(completedEvents, (evt) =>
    moment.utc(evt.start).tz(getTz(region)).format("MMMM YYYY")
  );

  const loadMorePastEvents = useCallback(
    (page = 0) => {
      const pageableParams = {
        size: PAGE_SIZE,
        sort: "start,desc",
        page,
      };

      loadCompletedEvents(
        user && user.id,
        "COMPLETED", // Not used
        pageableParams,
        user ? user.region : region /*undefined*/
      );

      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [user]
  );

  // So that when the user gets back, it'll reload the cards
  const handleEventCardClick = useCallback(() => {
    if (!user) {
      clearCompletedEventsLoaded();
    }
  }, [user]);

  return (
    <>
      <ScrollToTopOnMount />
      <Title>TruBe: Unique Locations, Unforgettable Fitness Events</Title>
      <Meta
        name="description"
        content="TruBe: Unique Locations, Unforgettable Fitness classes in Pilates, Yoga, HIIT, Cardio, and more. Providing the best coaches and fitness and wellbeing experience."
      />
      <LinkMeta rel="canonical" href="https://trubeapp.com/events/previous" />
      <Header
        // behaviour="none"
        showRegionSelect={false}
        isFixed
        isLoggedIn={isUserLoggedIn}
        returnTo="/lifestyle"
        heroRef={heroRef}
        onSignUpClick={clearCompletedEventsLoaded}
        onLoginClick={clearCompletedEventsLoaded}
      />
      <div />

      <div className="tbk-overflow-hidden">
        <div className="tbk-container tbk-mx-auto tbk-px-2 lg:tbk-px-8">
          <div
            ref={sliderRef}
            className="tb-slider tbk-mb-3 tbk-mt-3 tbk-flex tbk-flex-nowrap tbk-items-center"
          >
            <BreadcrumbNav to={"/lifestyle"}>
              Lifestyle collective
            </BreadcrumbNav>

            <ArrowRightIcon className="tbk-text-blue-grey-light" />

            <BreadcrumbNav>Previous Classes</BreadcrumbNav>
          </div>
        </div>
      </div>

      {Object.keys(pastEvents).length ? (
        <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pb-4 tbk-pt-4 lg:tbk-pt-4">
          <div className="tbk-container tbk-px-2 lg:tbk-px-8">
            <h3 className="tbk-text-title-bold lg:tbk-text-title-bold tbk-bg-basic-white tbk-px-2 tbk-text-center tbk-text-center tbk-text-blue-grey">
              Previous Classes
            </h3>
            {Object.keys(pastEvents).map((eventHeading) => (
              <div className="tbk-pt-4" key={eventHeading}>
                <div className="tbk-relative tbk-mb-4 tbk-text-center lg:tbk-mb-5">
                  <h2 className="tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-inline-block tbk-bg-basic-white tbk-px-2 tbk-text-center tbk-text-blue-grey">
                    {eventHeading}
                  </h2>
                  <hr className="tbk--mt-2 tbk-mb-0 tbk-border-blue-grey-light lg:tbk--mt-2" />
                </div>
                <div className="tbk-mb-4 tbk-grid tbk-gap-2 md:tbk-grid-cols-2 lg:tbk-grid-cols-3 ">
                  {pastEvents[eventHeading].map((evt, index) => (
                    <EventCard
                      key={evt.id}
                      href={`/events/${evt.id}`}
                      imgUrl={evt.coverPhoto}
                      title={evt.name}
                      startDate={evt.start}
                      endDate={evt.finish}
                      place={evt.location.name}
                      joined={evt.joined}
                      soldOut={evt.capacity === evt.currentCapacity}
                      status={evt.status}
                      region={region}
                      timezone={evt.timezone}
                      onClick={handleEventCardClick}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="tbk-min-h-[370px]" />
      )}

      {hasMorePastEvents && !isPastEventsFetching ? (
        <Button
          variant="secondary"
          className="tbk-mt-7_ tbk-mx-auto tbk-mb-2 tbk-mb-7 tbk-w-68 md:tbk-mb-7"
          onClick={() => loadMorePastEvents(pastEventsPageLoaded + 1)}
        >
          Show more
        </Button>
      ) : null}

      <div className="tbk-mb-7 tbk-flex tbk-justify-center">
        <Link to={"/lifestyle"}>
          <Button
            className="tbk-w-40 tbk-text-primary"
            variant="secondary"
            size={"md"}
            startIcon={<ArrowLeftIcon className="tbk-tex-primary" />}
          >
            Back
          </Button>
        </Link>
      </div>

      <Footer regionSelect={false} />
    </>
  );
}
